import {
  CurrencyPipe,
  DatePipe,
  DecimalPipe,
  PercentPipe,
  NgTemplateOutlet,
} from '@angular/common';
import {
  Component,
  HostListener,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  TemplateRef,
  contentChild,
  forwardRef,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import {
  VOneSearchComponent,
  VOneFilterComponent,
  VOneButtonComponent,
  VOnePaginatorComponent,
  VOneIconComponent,
  VOneTableCellComponent,
} from '@shared-components';
import { TooltipDirective } from '@shared-directives';
import { IconType } from '@shared-enums';
import {
  TableConfig,
  TableColumn,
  SortDirection,
  SortEvent,
  DropdownOption,
  HeaderIcon,
  CellConfig,
  TableAction,
  TagState,
} from '@shared-models';

/**
 * Matches the `sm` breakpoint in src/styles/tokens/_breakpoints.scss: below
 * it the toolbar is on a phone, where the register button is icon-only.
 */
const NARROW_VIEWPORT_PX = 576;

@Component({
  selector: 'v-one-dynamic-table',
  standalone: true,
  imports: [
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    PercentPipe,
    NgTemplateOutlet,
    TranslateModule,
    forwardRef(() => VOneSearchComponent),
    forwardRef(() => VOneFilterComponent),
    forwardRef(() => VOneButtonComponent),
    forwardRef(() => VOnePaginatorComponent),
    forwardRef(() => VOneIconComponent),
    forwardRef(() => VOneTableCellComponent),
    TooltipDirective,
  ],
  templateUrl: './v-one-dynamic-table.component.html',
  styleUrls: ['./v-one-dynamic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneDynamicTableComponent<T> {
  readonly ICON_TYPE = IconType;

  data = input.required<T[]>();
  tableConfig = input.required<TableConfig<T>>();
  loading = input<boolean>(false);

  searchable = input<boolean>(true);
  searchPlaceholder = input<string>('Search...');
  /** Idle time before a typed term is emitted, in milliseconds. */
  searchDebounce = input<number>(1000);

  filterable = input<boolean>(false);
  filterOptions = input<DropdownOption[]>([]);
  filterPlaceholder = input<string>('Filter');
  showFilterChips = input<boolean>(true);

  showRegisterButton = input<boolean>(false);
  registerButtonText = input<string>('Register');
  registerButtonIcon = input<string>('add');

  /**
   * Minimum width of the table itself, e.g. '1400px'. Left unset the table
   * keeps the width its stylesheet gives it; set wider than the container it
   * turns on the horizontal scroll instead of squeezing the columns.
   */
  tableMinWidth = input<string>('');

  /**
   * Translation key of the actions column header. Passing an empty string
   * leaves that header blank.
   */
  actionsLabel = input<string>('common.actions');

  /**
   * Toolbar arrangement. 'stacked' keeps search and the register button on
   * one row with the filter below. 'inline' lets the filter share the row
   * from desktop up and collapses the register button to its icon on a
   * narrow viewport.
   */
  toolbarLayout = input<'stacked' | 'inline'>('stacked');

  /**
   * Keeps the rows on screen while a reload is in flight, dimmed and not
   * interactive, instead of replacing the whole table with the loading
   * message. The message still covers the first load, when there is nothing
   * to keep.
   */
  keepDataWhileLoading = input<boolean>(false);

  /** True while the viewport is narrower than the tablet breakpoint. */
  private readonly isNarrowViewport = signal<boolean>(
    typeof window !== 'undefined'
      ? window.innerWidth < NARROW_VIEWPORT_PX
      : false,
  );

  /**
   * The inline toolbar drops the register button's text on a narrow
   * viewport; VOneButtonComponent renders a round icon-only button when it
   * gets an icon and no text.
   */
  readonly registerLabel = computed(() =>
    this.toolbarLayout() === 'inline' && this.isNarrowViewport()
      ? ''
      : this.registerButtonText(),
  );

  @HostListener('window:resize')
  onWindowResize(): void {
    if (typeof window !== 'undefined') {
      this.isNarrowViewport.set(window.innerWidth < NARROW_VIEWPORT_PX);
    }
  }

  pageable = input<boolean>(true);
  totalItems = input<number>(0);
  currentPage = input<number>(1);
  pageSize = input<number>(10);
  pageSizeOptions = input<number[]>([10, 25, 50, 100]);

  expandedTemplate = contentChild('expandedRow', {
    read: TemplateRef<{ $implicit: T }>,
  });

  searchChange = output<string>();
  filterChange = output<string[]>();
  registerClick = output<void>();
  sortChange = output<SortEvent>();
  pageChange = output<number>();
  itemsPerPageChange = output<number>();
  actionClick = output<{ action: string; item: T }>();
  selectionChange = output<T[]>();
  cellEdit = output<{
    item: T;
    column: TableColumn<T>;
    value: string | number;
  }>();

  readonly sortColumn = signal<string | null>(null);
  readonly sortDirection = signal<SortDirection>(null);
  readonly selectedItems = signal<Set<T>>(new Set());
  readonly expandedItems = signal<Set<T>>(new Set());

  readonly tableVariant = computed(() => {
    return this.tableConfig().variant || 'simple';
  });

  readonly hasData = computed(() => {
    return this.data() && this.data().length > 0;
  });

  /** A reload over existing rows keeps the table instead of replacing it. */
  private readonly reloadsInPlace = computed(
    () => this.keepDataWhileLoading() && this.hasData(),
  );

  readonly showsLoadingState = computed(
    () => this.loading() && !this.reloadsInPlace(),
  );

  readonly showsTable = computed(
    () => this.hasData() && (!this.loading() || this.reloadsInPlace()),
  );

  readonly columns = computed(() => {
    return this.tableConfig().columns || [];
  });

  readonly actions = computed(() => {
    return this.tableConfig().actions || [];
  });

  readonly showIndex = computed(() => {
    return this.tableConfig().showIndex ?? false;
  });

  readonly indexLabel = computed(() => {
    return this.tableConfig().indexLabel ?? 'No.';
  });

  readonly hasActions = computed(() => {
    return this.actions().length > 0;
  });

  /** Why the action is off for this row; empty while it is available. */
  disabledTooltip(action: TableAction<T>, item: T): string {
    if (!this.isActionDisabled(action, item)) return '';

    return action.disabledTooltip?.(item) ?? '';
  }

  readonly allSelected = computed(() => {
    const dataItems = this.data();
    const selected = this.selectedItems();
    return (
      dataItems.length > 0 && dataItems.every((item) => selected.has(item))
    );
  });

  onSearchChange(searchTerm: string): void {
    this.searchChange.emit(searchTerm);
  }

  onFilterChange(values: string[]): void {
    this.filterChange.emit(values);
  }

  onRegisterClick(): void {
    this.registerClick.emit();
  }

  onSort(column: TableColumn<T>): void {
    if (!column.sortable) return;

    const currentColumn = this.sortColumn();
    const currentDirection = this.sortDirection();

    let newDirection: SortDirection;

    if (currentColumn === column.key) {
      if (currentDirection === 'asc') {
        newDirection = 'desc';
      } else if (currentDirection === 'desc') {
        newDirection = null;
      } else {
        newDirection = 'asc';
      }
    } else {
      newDirection = 'asc';
    }

    this.sortColumn.set(newDirection ? column.key : null);
    this.sortDirection.set(newDirection);

    this.sortChange.emit({
      column: column.key,
      direction: newDirection,
    });
  }

  onHeaderIconClick(headerIcon: HeaderIcon, event: Event): void {
    event.stopPropagation();
    if (headerIcon.clickHandler) {
      headerIcon.clickHandler();
    }
  }

  getSortIcon(column: TableColumn<T>): string {
    if (!column.sortable) return '';

    const currentColumn = this.sortColumn();
    const currentDirection = this.sortDirection();

    if (currentColumn !== column.key) {
      return 'sort';
    }

    if (currentDirection === 'asc') {
      return 'arrowUp';
    } else if (currentDirection === 'desc') {
      return 'arrowDown';
    }

    return 'sort';
  }

  isSortActive(column: TableColumn<T>): boolean {
    return this.sortColumn() === column.key && this.sortDirection() !== null;
  }

  getCellValue(item: T, column: TableColumn<T>): string | number {
    if (column.customRenderer) {
      return column.customRenderer(item);
    }

    const keys = column.key.split('.');
    let value: unknown = item;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        return '';
      }
    }

    if (value === null || value === undefined) {
      return '';
    }

    return value as string | number;
  }

  getRowIndex(index: number): number {
    if (this.pageable()) {
      return (this.currentPage() - 1) * this.pageSize() + index + 1;
    }
    return index + 1;
  }

  shouldShowAction(
    action: { condition?: (item: T) => boolean },
    item: T,
  ): boolean {
    if (!action.condition) return true;
    return action.condition(item);
  }

  isActionDisabled(action: TableAction<T>, item: T): boolean {
    if (!action.disabled) return false;
    return action.disabled(item);
  }

  onActionClick(action: TableAction<T>, item: T): void {
    if (this.isActionDisabled(action, item)) {
      return;
    }

    action.handler(item);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onItemsPerPageChange(size: number): void {
    this.itemsPerPageChange.emit(size);
  }

  trackByFn(index: number, item: T): string | number {
    const config = this.tableConfig();
    if (config.trackByFn) {
      return config.trackByFn(index, item);
    }
    return index;
  }

  // Multiselect methods
  isSelected(item: T): boolean {
    return this.selectedItems().has(item);
  }

  toggleSelection(item: T): void {
    const selected = new Set(this.selectedItems());
    if (selected.has(item)) {
      selected.delete(item);
    } else {
      selected.add(item);
    }
    this.selectedItems.set(selected);
    this.selectionChange.emit(Array.from(selected));
  }

  toggleSelectAll(): void {
    const dataItems = this.data();
    const selected = new Set(this.selectedItems());

    if (this.allSelected()) {
      // Deselect all
      dataItems.forEach((item) => selected.delete(item));
    } else {
      // Select all
      dataItems.forEach((item) => selected.add(item));
    }

    this.selectedItems.set(selected);
    this.selectionChange.emit(Array.from(selected));
  }

  // Expandable row methods
  isExpanded(item: T): boolean {
    return this.expandedItems().has(item);
  }

  toggleExpand(item: T): void {
    const expanded = new Set(this.expandedItems());
    if (expanded.has(item)) {
      expanded.delete(item);
    } else {
      expanded.add(item);
    }
    this.expandedItems.set(expanded);
  }

  getColspan(): number {
    let colspan = this.columns().length;

    if (this.tableVariant() === 'multiselect') colspan++;
    if (this.tableVariant() === 'expandable') colspan++;
    if (this.showIndex()) colspan++;
    if (this.hasActions()) colspan++;

    return colspan;
  }

  // Editable cell methods
  onCellEdit(item: T, column: TableColumn<T>, value: string): void {
    if (column.onCellEdit) {
      column.onCellEdit(item, value);
    }
    this.cellEdit.emit({ item, column, value });
  }

  // Cell variant methods
  getCellConfig(item: T, column: TableColumn<T>): CellConfig {
    if (!column.cellConfig) {
      return { variant: 'default' };
    }

    const cellValue = this.getCellValue(item, column);
    const config = { ...column.cellConfig };

    // Tags read their state from the row, so a label containing '|' must not
    // fall into the multi-line parsing below.
    if (config.variant === 'tag') {
      config.tagText = String(cellValue);
      config.text = config.tagText;

      const tagData = this.getNestedValue(item, column.key);
      if (tagData && typeof tagData === 'object' && 'state' in tagData) {
        config.tagState = (tagData as { state: TagState }).state;
      }

      return config;
    }

    // Parse the cell value to populate the config
    if (typeof cellValue === 'string' && cellValue.includes('|')) {
      const parts = cellValue.split('|');

      // Handle multi-line variant
      if (config.variant === 'multi-line' || config.variant === 'double-line') {
        config.primaryText = parts[0] || '';
        config.secondaryText = parts[1] || '';
      }

      // Handle single-line variant, whose second half follows the chevron.
      // Only an inline chevron asks for two halves, so every other
      // single-line cell keeps whatever '|' its value carries.
      if (
        config.variant === 'single-line' &&
        config.chevronPosition === 'inline'
      ) {
        config.text = parts[0] || '';
        config.primaryText = config.text;
        config.secondaryText = parts[1] || '';
      }

      // Handle with-remark variant
      if (config.variant === 'with-remark') {
        config.text = parts[0] || '';
        config.remark = parts[1] || '';
        config.description = parts[1] || '';
      }
    } else {
      // Single value variants
      if (config.variant === 'single-line') {
        config.text = String(cellValue);
        config.primaryText = String(cellValue);
      } else if (config.variant === 'status') {
        config.statusText = String(cellValue);
        config.text = String(cellValue);
        // Get status type from item data if available
        const statusData = this.getNestedValue(item, column.key);
        if (
          statusData &&
          typeof statusData === 'object' &&
          'type' in statusData
        ) {
          config.statusType = (statusData as { type: string }).type as
            | 'success'
            | 'warning'
            | 'error'
            | 'info';
          config.status = config.statusType;
        }
      } else if (
        config.variant === 'action' ||
        config.variant === 'action-icon'
      ) {
        config.actionText = String(cellValue);
        config.actionLabel = String(cellValue);
        // Get action icon from item data if available
        const actionData = this.getNestedValue(item, column.key);
        if (
          actionData &&
          typeof actionData === 'object' &&
          'icon' in actionData
        ) {
          config.actionIcon = (actionData as { icon: string }).icon;
        }
      } else if (config.variant === 'toggle') {
        const toggleData = this.getNestedValue(item, column.key);
        config.toggleValue = Boolean(toggleData);
      } else if (config.variant === 'checkbox') {
        const checkboxData = this.getNestedValue(item, column.key);
        config.checkboxValue = Boolean(checkboxData);
      }
    }

    return config;
  }

  private getNestedValue(item: T, key: string): unknown {
    const keys = key.split('.');
    let value: unknown = item;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return undefined;
      }
    }

    return value;
  }
}
